import { useEffect, useRef, useState } from "react";
import { Box, IconButton, InputBase, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import type { MediaFile } from "../../../__generated__/types";
import { TEAM_EMOJIS } from "../messages-constants";
import { getMessagePreview } from "../messages-utils";

type Props = {
  threadId?: string | null;
  messageDraft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  canSend: boolean;
  messagingBlocked: boolean;
  messagingBlockedReason?: string | null;
  checkingMessagePermission?: boolean;
  sendingMessage: boolean;
  uploadingFiles: boolean;
  pendingAttachments: MediaFile[];
  onRemoveAttachment: (id?: string | null) => void;
  onFileUpload: (files: FileList | null) => void;
  replyTarget: any | null;
  onCancelReply: () => void;
  resolveParticipantName: (
    senderId?: string | null,
    senderRole?: string | null
  ) => string;
};

const MessageComposer = ({
  threadId,
  messageDraft,
  onDraftChange,
  onSend,
  canSend,
  messagingBlocked,
  messagingBlockedReason = null,
  checkingMessagePermission = false,
  sendingMessage,
  uploadingFiles,
  pendingAttachments,
  onRemoveAttachment,
  onFileUpload,
  replyTarget,
  onCancelReply,
  resolveParticipantName,
}: Props) => {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const audioInputRef = useRef<HTMLInputElement | null>(null);
  const composerDisabled = messagingBlocked || checkingMessagePermission;

  useEffect(() => {
    setEmojiOpen(false);
  }, [threadId]);

  const handleEmojiSelect = (emoji: string) => {
    onDraftChange(`${messageDraft}${emoji}`.slice(0, 200));
    setEmojiOpen(false);
  };

  return (
    <Box className="messages-thread-input">
      {replyTarget && (
        <Box className="messages-reply-preview">
          <Box className="messages-reply-content">
            <Typography className="messages-reply-label">
              Replying to{" "}
              {resolveParticipantName(
                replyTarget?.senderId,
                replyTarget?.senderRole
              )}
            </Typography>
            <Typography className="messages-reply-snippet">
              {getMessagePreview(replyTarget)}
            </Typography>
          </Box>
          <button
            type="button"
            className="messages-reply-close"
            onClick={onCancelReply}
            aria-label="Cancel reply"
          >
            x
          </button>
        </Box>
      )}
      {pendingAttachments.length > 0 && (
        <Box className="messages-attachment-row">
          {pendingAttachments.map((file) => (
            <Box key={file._id} className="messages-attachment-chip">
              <span className="messages-attachment-name">
                {file.title || file.name || "Attachment"}
              </span>
              <button
                type="button"
                className="messages-attachment-remove"
                onClick={() => onRemoveAttachment(file._id)}
                aria-label="Remove attachment"
              >
                x
              </button>
            </Box>
          ))}
        </Box>
      )}
      <Box className="messages-input-row">
        <Box className="messages-input-actions">
          <IconButton
            aria-label="Attach audio"
            onClick={() => audioInputRef.current?.click()}
            className="icon-muted"
            disabled={composerDisabled}
          >
            <MicNoneOutlinedIcon />
          </IconButton>
          <IconButton
            aria-label="Attach image"
            onClick={() => imageInputRef.current?.click()}
            className="icon-muted"
            disabled={composerDisabled}
          >
            <ImageOutlinedIcon />
          </IconButton>
          <IconButton
            aria-label="Add emoji"
            onClick={() => setEmojiOpen((prev) => !prev)}
            className={`icon-muted ${emojiOpen ? "is-active" : ""}`}
            disabled={composerDisabled}
          >
            <EmojiEmotionsOutlinedIcon />
          </IconButton>
        </Box>
        <InputBase
          className="messages-input"
          placeholder="Type message"
          value={messageDraft}
          onChange={(event) => onDraftChange(event.target.value)}
          disabled={composerDisabled}
          onKeyDown={(event) => {
            if ((event.nativeEvent as any)?.isComposing) return;
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              if (!composerDisabled && !sendingMessage && !uploadingFiles && canSend) {
                onSend();
              }
            }
          }}
          inputProps={{ maxLength: 200 }}
        />
        <IconButton
          className={`messages-send-button ${canSend ? "is-active" : ""}`}
          onClick={onSend}
          disabled={composerDisabled || sendingMessage || uploadingFiles || !canSend}
          aria-label="Send message"
        >
          <SendIcon />
        </IconButton>
      </Box>
      {messagingBlocked && (
        <Typography className="messages-blocked-reason">
          {messagingBlockedReason || "Messaging is unavailable for this match."}
        </Typography>
      )}
      {emojiOpen && (
        <Box className="messages-emoji-picker">
          {TEAM_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="messages-emoji"
              onClick={() => handleEmojiSelect(emoji)}
            >
              {emoji}
            </button>
          ))}
        </Box>
      )}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="messages-hidden-input"
        disabled={composerDisabled}
        onChange={(event) => {
          onFileUpload(event.target.files);
          if (event.target.value) event.target.value = "";
        }}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        multiple
        className="messages-hidden-input"
        disabled={composerDisabled}
        onChange={(event) => {
          onFileUpload(event.target.files);
          if (event.target.value) event.target.value = "";
        }}
      />
    </Box>
  );
};

export default MessageComposer;
